import type { AxiosInstance } from 'axios';
import { makePersonaPath, type ServiceOptions } from './personaPath';
export interface FileReference {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  url: string;
  createdAt: string;
  workflow?: string;
}

/**
 * Owning organization / tenant of the RESOURCE a file belongs to — never the
 * uploader's. file-management-service reads both from multipart form fields on
 * `POST /v1/files` (see `resolveOwningOrganization` / `parseOwningTenant` in
 * `backend/file-management-service/internal/handler/file_handler.go`) precisely
 * because the caller's headers carry whoever pressed upload: a Workpay admin
 * uploading a client's document carries the Workpay org and no tenant of its own.
 */
export interface FileOwner {
  organizationId?: string | null;
  tenantId?: string | null;
}

/**
 * Supplies the owner to stamp on uploads that don't name one explicitly. Host
 * apps with a session (tenant-onboarding) pass a resolver reading the session
 * scope; token-scoped external apps (ds-external) pass nothing and the server
 * keeps its existing header fallback.
 */
export type FileOwnerResolver = () => FileOwner;

export function createFileService(
  client: AxiosInstance,
  resolveOwner?: FileOwnerResolver,
  opts?: ServiceOptions,
) {
  const personaPath = makePersonaPath(opts);
  return {
    /**
     * @param owner owning org/tenant of the resource this file belongs to. Take
     *              it off the loaded entity whenever the caller may be acting for
     *              someone else; omit it to fall back to the host app's resolver.
     */
    upload: async (file: File, workflow?: string, owner?: FileOwner): Promise<FileReference> => {
      const formData = new FormData();
      formData.append('file', file);
      if (workflow) {
        formData.append('workflow', workflow);
      }
      const resolved: FileOwner =
        owner?.organizationId || owner?.tenantId ? owner : (resolveOwner?.() ?? {});
      if (resolved.organizationId) formData.append('organizationId', resolved.organizationId);
      if (resolved.tenantId) formData.append('tenantId', resolved.tenantId);
      const { data } = await client.post<FileReference>(personaPath('files'), formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    getFileUrl: (fileId: string): string => {
      const base = client.defaults.baseURL ?? '';
      // Backend gap: no persona copy (2026-10-27).
      return `${base}/v1/files/${fileId}`;
    },

    download: (fileId: string) =>
      // Backend gap: no persona copy (2026-10-27).
      client.get<Blob>(`/v1/files/${fileId}`, { responseType: 'blob' }),

    delete: (fileId: string) => client.delete(personaPath(`files/${fileId}`)),

    /**
     * Server-side convert a DOCX file to PDF (used to show uploaded-DOCX previews
     * in react-pdf without shipping a docx renderer client-side). Matches
     * document-signing's `POST /v1/files/{id}/convert-to-pdf` route.
     */
    convertToPdf: (fileId: string) =>
      // Backend gap: no persona copy (2026-10-27).
      client.post<Blob>(`/v1/files/${fileId}/convert-to-pdf`, null, {
        responseType: 'blob',
      }),

    /**
     * Stamp placeholder values into a DOCX file and return the resulting PDF.
     * Used by the envelope wizard when entering Place Fields so the field
     * overlay is drawn on top of the filled PDF, not the raw template.
     */
    generatePdf: (fileId: string, placeholderValues: Record<string, string>) =>
      // Backend gap: no persona copy (2026-10-27).
      client.post<Blob>(`/v1/files/${fileId}/generate-pdf`, placeholderValues, {
        responseType: 'blob',
      }),
  };
}
